/**
 * QualityBadge — renders an A/B/C/D quality grade pill.
 * SPEC-013 · Transcript Quality UX
 *
 * Displays the quality grade computed by computeQualityScore() server-side.
 * Colour-coded: A=emerald, B=teal, C=amber, D=red.
 */

// React is used implicitly via JSX transform

export type QualityGrade = 'A' | 'B' | 'C' | 'D';

interface QualityBadgeProps {
  grade: QualityGrade;
  avgConf?: number;
  /** Show full label alongside the grade letter */
  showLabel?: boolean;
  /** Extra Tailwind classes */
  className?: string;
}

const GRADE_CONFIG: Record<QualityGrade, { label: string; bgColour: string; title: string }> = {
  A: {
    label:    'Excellent',
    bgColour: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
    title:    'High-confidence transcription. Minimal errors expected.',
  },
  B: {
    label:    'Good',
    bgColour: 'bg-teal-100 text-teal-700 border border-teal-300',
    title:    'Good transcription quality. A few words may need review.',
  },
  C: {
    label:    'Fair',
    bgColour: 'bg-amber-100 text-amber-700 border border-amber-300',
    title:    'Moderate quality. Review speaker-labelled sections carefully.',
  },
  D: {
    label:    'Poor',
    bgColour: 'bg-red-100 text-red-700 border border-red-300',
    title:    'Low confidence or heavy repetition detected. Manual correction recommended.',
  },
};

export function QualityBadge({ grade, avgConf, showLabel = false, className = '' }: QualityBadgeProps) {
  const cfg = GRADE_CONFIG[grade];
  const confPct = avgConf != null ? ` · ${Math.round(avgConf * 100)}% conf` : '';
  const tooltip = cfg.title + confPct;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.bgColour} ${className}`}
      title={tooltip}
      aria-label={`Transcript quality: ${cfg.label}${confPct}`}
    >
      <span className="font-bold">{grade}</span>
      {showLabel && <span>· {cfg.label}</span>}
    </span>
  );
}
